import * as React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
const EMPTY_FORM = {
  prestamo: null,
  fecha_promesa: '',
  monto_prometido: '',
  notas: '',
  estado: 'pendiente',
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

export default function CrearPromesaPagoDialog({ open, onClose, onSave, initial, defaultPrestamo }) {
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [touched, setTouched] = React.useState({});
  const [prestamosActivos, setPrestamosActivos] = React.useState([]);
  const [loadingPrestamos, setLoadingPrestamos] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const prestamoLocked = Boolean(initial || defaultPrestamo);

  React.useEffect(() => {
    if (!open || prestamoLocked) return;
    setLoadingPrestamos(true);
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/activos/${localStorage.getItem('idcompany')}`, {
      headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((data) => setPrestamosActivos(Array.isArray(data) ? data : []))
      .catch(() => setPrestamosActivos([]))
      .finally(() => setLoadingPrestamos(false));
  }, [open, prestamoLocked]);

  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        prestamo: { loan_id: initial.loan_id, prestamo_original_id: initial.id_prestamo, cliente_nombre: initial.cliente_nombre, cliente_identificacion: initial.cliente_identificacion },
        fecha_promesa: initial.fecha_promesa ? initial.fecha_promesa.substring(0, 10) : '',
        monto_prometido: initial.monto_prometido?.toString() || '',
        notas: initial.notas || '',
        estado: initial.estado || 'pendiente',
      });
    } else if (defaultPrestamo) {
      setForm({ ...EMPTY_FORM, prestamo: defaultPrestamo });
    } else {
      setForm(EMPTY_FORM);
    }
    setTouched({});
  }, [open, initial, defaultPrestamo]);

  const isEdit = Boolean(initial);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const markTouched = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const prestamoError = touched.prestamo && !form.prestamo;
  const fechaError = touched.fecha_promesa && !form.fecha_promesa;
  const montoError = touched.monto_prometido && (!form.monto_prometido || parseFloat(form.monto_prometido) <= 0);

  const handleSave = async () => {
    if (!form.prestamo || !form.fecha_promesa || !form.monto_prometido || parseFloat(form.monto_prometido) <= 0) {
      setTouched({ prestamo: true, fecha_promesa: true, monto_prometido: true });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          fecha_promesa: form.fecha_promesa,
          monto_prometido: parseFloat(form.monto_prometido),
          estado: form.estado,
          notas: form.notas.trim(),
        };
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/promesas_pago/${initial.id}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error((await res.json())?.error || 'Error al actualizar la promesa');
      } else {
        const body = {
          idcompany: localStorage.getItem('idcompany'),
          id_prestamo: form.prestamo.prestamo_original_id,
          fecha_promesa: form.fecha_promesa,
          monto_prometido: parseFloat(form.monto_prometido),
          notas: form.notas.trim(),
          created_by: localStorage.getItem('id'),
        };
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/promesas_pago`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error((await res.json())?.error || 'Error al crear la promesa');
      }
      onSave?.();
      onClose?.();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setTouched({});
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Editar promesa de pago' : 'Nueva promesa de pago'}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
              Préstamo
            </Typography>
            <Autocomplete
              disabled={prestamoLocked}
              loading={loadingPrestamos}
              options={prestamosActivos}
              value={form.prestamo}
              onChange={(_, value) => setField('prestamo', value)}
              onBlur={() => markTouched('prestamo')}
              getOptionLabel={(option) => `${option.loan_id || ''} · ${option.cliente_nombre || ''}`}
              isOptionEqualToValue={(a, b) => a.loan_id === b.loan_id}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
                  <Typography variant="body2" fontWeight={600}>{option.loan_id} — {option.cliente_nombre}</Typography>
                  {option.cliente_identificacion && (
                    <Typography variant="caption" color="text.secondary">Ced: {option.cliente_identificacion}</Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Buscar préstamo o cliente" error={prestamoError} helperText={prestamoError ? 'Seleccione un préstamo' : ''} />
              )}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item size={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
                Fecha de promesa
              </Typography>
              <TextField
                size="small"
                fullWidth
                type="date"
                value={form.fecha_promesa}
                onChange={(e) => setField('fecha_promesa', e.target.value)}
                onBlur={() => markTouched('fecha_promesa')}
                error={fechaError}
                helperText={fechaError ? 'Fecha requerida' : ''}
              />
            </Grid>
            <Grid item size={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
                Monto prometido
              </Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={form.monto_prometido}
                onChange={(e) => setField('monto_prometido', e.target.value)}
                onBlur={() => markTouched('monto_prometido')}
                error={montoError}
                helperText={montoError ? 'Monto mayor a 0' : ''}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">RD$</InputAdornment> } }}
              />
            </Grid>
          </Grid>

          {isEdit && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
                Estado
              </Typography>
              <TextField
                size="small"
                fullWidth
                select
                value={form.estado}
                onChange={(e) => setField('estado', e.target.value)}
              >
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="cumplida">Cumplida</MenuItem>
                <MenuItem value="incumplida">Incumplida</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </TextField>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
              Notas
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              placeholder="Observaciones del cliente, motivo, detalles..."
              value={form.notas}
              onChange={(e) => setField('notas', e.target.value)}
            />
          </Box>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={handleClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primarybutton" onClick={handleSave} disabled={saving} sx={{ fontWeight: 700 }}>
          {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Registrar promesa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
